<template>
  <div class="assignment-container">
    <div class="assignment-tab-body" v-if="!(assignments && assignments.length !== 0)">
      <div>
        <div
          v-if="assignments && assignments.length !== 0"
          style="display: flex; justify-content: flex-end; align-items: center; margin-bottom: -50px"
        >
          <div v-if="!isOrganizationK12" class="create-new-assignment">
            <router-link :to="'/organization/' + organizationId + '/assignments/new'" tag="button">
              <span>
                {{ $t("assignments.add_assignment") }}
              </span>
            </router-link>
          </div>
        </div>
      </div>
    </div>
    <table-layout
      :showTitle="false"
      :pager-counter="pageCounter"
      :pageSize="numberElementsPerPage"
      :total="pagination.total"
      :currentChange="getDataSetPage"
      :current-page.sync="currentPage"
      :queryString.sync="queryString"
      :selected-data-source.sync="selectedDataSource"
      :query-placeholder="$t('common.search')"
      :search="searchData"
      :searchable="true"
      :sortable="true"
      :sortFuncSpec="sortFuncSpec"
      :ordering.sync="ordering"
      :title="$t('study_sets.curriculum')"
      v-loading="loading"
      :dataSource="assignments"
      :request="getDatas"
      ref="table"
    >
      <template slot="alert-actions">
        <el-button class="delete-assignment" @click="handleDeleteSelectedStudySets">
          {{ $t("common.remove") }}
        </el-button>
      </template>
      <template slot="toolBar">
        <div class="export-report">
          <el-tooltip placement="top" content="Select the assignment(s) you want to export report for.">
            <el-dropdown @command="handleCommand">
              <el-button type="primary" :disabled="disabledExport">
                {{ $t("assignments.report.export_report") }}<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="overall">
                  {{ $t("assignments.report.overall_report") }}
                </el-dropdown-item>
                <el-dropdown-item command="detailed" :disabled="disabledDetailExport">
                  {{ $t("assignments.report.detailed_report") }}
                </el-dropdown-item>
                <el-dropdown-item command="both" :disabled="disabledDetailExport">
                  {{ $t("assignments.report.both") }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </el-tooltip>
        </div>
        <div v-if="!isOrganizationK12" class="create-new-assignment">
          <el-button @click="handleAddAssignment">
            {{ $t("assignments.add_assignment") }}
          </el-button>
        </div>
      </template>
      <template slot="cols">
        <el-table-column :label="$t('assignments.table.name')" width="180">
          <template slot-scope="scope">
            <router-link
              :to="
                '/organization/' + organizationId + '/classes/' + getCurrentClass.id + '/assignments/' + scope.row.id
              "
            >
              <span style="font-size: 16px">{{ scope.row.name }}</span>
            </router-link>
          </template>
        </el-table-column>
        <el-table-column :label="$t('assignments.table.timeline')" align="center" width="120">
          <template slot-scope="scope">
            {{
              $t("common.from_date_to_up_date", {
                from_date: formatDate(scope.row.start_date),
                to_date: formatDate(scope.row.due_date),
              })
            }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('assignments.table.max_score')" align="center" width="120">
          <template slot-scope="scope"> {{ formatScore(scope.row.max_score) }} </template>
        </el-table-column>
        <el-table-column :label="$t('assignments.table.min_score')" align="center" width="120">
          <template slot-scope="scope"> {{ formatScore(scope.row.min_score) }} </template>
        </el-table-column>
        <el-table-column :label="$t('assignments.table.avg_score')" align="center" width="120">
          <template slot-scope="scope"> {{ formatScore(scope.row.avg_score) }} </template>
        </el-table-column>
        <el-table-column :label="$t('assignments.table.submitted_ontime')" align="center" width="120">
          <template slot-scope="scope"> {{ scope.row.submited_ontime }} </template>
        </el-table-column>
        <el-table-column :label="$t('assignments.table.submitted_late')" align="center" width="120">
          <template slot-scope="scope"> {{ scope.row.submited_late }} </template>
        </el-table-column>
        <el-table-column :label="$t('assignments.table.missed')" align="center" width="120">
          <template slot-scope="scope"> {{ scope.row.missed }} </template>
        </el-table-column>
        <el-table-column width="60">
          <template slot-scope="scope">
            <div class="flex">
              <tertiary-button class="close-button" @click.native="handleDeleteStudySet(scope.row.id)">
                <font-awesome-icon
                  :icon="['fas', 'trash-alt']"
                  style="color: #eb5757; font-size: 14px; line-height: 14px"
                />
              </tertiary-button>
            </div>
          </template>
        </el-table-column>
      </template>
    </table-layout>
    <confirm-delete-assignment-modal
      page="list"
      :dialogVisible="modals.showConfirmDelete"
      append-to-body
      v-on:closed="modals.showConfirmDelete = false"
      v-on:delete="handleConfirmDeleteAssignment"
    />
    <no-study-set-modal
      :dialogVisible="modals.showNoStudySet"
      append-to-body
      v-on:closed="modals.showNoStudySet = false"
      v-on:create-study-set="handleCreateStudySet"
    />
    <add-assignment-wrapper ref="addAssignment" v-on:reload="handleExit" />
  </div>
</template>

<script>
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { mapGetters, mapActions, mapState } from "vuex";
import { successMessage, warningMessage } from "@/utils/messages";
import TableLayout from "@/components/common/v2/ElsaBaseTableLayout";
import { always, pathOr } from "ramda";
import { NUMBER_ELEMENTS_PER_PAGE, PAGE_COUNTER, DATE_FORMAT } from "@/constants/generic";
import TertiaryButton from "@/components/common/buttons/TertiaryButton";
import ConfirmDeleteAssignmentModal from "@/components/modals/assignments/ConfirmDeleteAssignmentModal";
import NoStudySetModal from "@/components/modals/assignments/NoStudySetModal";
import AddAssignmentWrapper from "@/components/modals/studysets/AddAssignmentWrapper";
import { utils, writeFile } from "xlsx-js-style";
import showNoReportDataWarning from "@/components/common/mixins/noReportData";
import { isSessionExpireError } from "@/utils/sessionExpireErr";
import { assignmentStatus } from "@/constants/studySets";
import {
  processLessonDetailedReportData,
  processLessonAssignmentsData,
  processSSDetailedReportData,
  processReadAloudDetailedReportData,
  processReadAloudLessonAssignmentsData,
  processAITutorDetailedReportData,
  processAITutorAssignmentsData,
} from "../DetailReportUtils";

export default {
  mixins: [showNoReportDataWarning],
  components: {
    TableLayout,
    TertiaryButton,
    ConfirmDeleteAssignmentModal,
    AddAssignmentWrapper,
    NoStudySetModal,
  },
  props: ["organizationType", "groupId"],
  data() {
    return {
      dateFormat: DATE_FORMAT,
      modals: {
        showConfirmDelete: false,
        showNoStudySet: false,
      },
      deleteStudySetIds: [],
      activeEntry: "1",
      currentPage: 1,
      queryString: null,
      ordering: "-start_date",
      loading: true,
      showInviteTeacherToTeamModal: false,
      currentClassInvite: null,
      selectedDataSource: [],
      headerColumnAss: [
        { v: "Student Name", t: "s", s: { font: { bold: true } } },
        { v: "Student Email", t: "s", s: { font: { bold: true } } },
        { v: "Assignment Status", t: "s", s: { font: { bold: true } } },
        { v: "Submission", t: "s", s: { font: { bold: true } } },
        { v: "First Completion Date", t: "s", s: { font: { bold: true } } },
        { v: "First Completion Time", t: "s", s: { font: { bold: true } } },
        { v: "Highest Score", t: "s", s: { font: { bold: true } } },
      ],
      headerFieldAss: [
        "full_name",
        "email",
        "status",
        "submission",
        "first_complete_date",
        "first_complete_time",
        "highest_score",
      ],
      headerColumnOverall: [
        { v: "Student Name", t: "s", s: { font: { bold: true } } },
        { v: "Student Email", t: "s", s: { font: { bold: true } } },
        { v: "Submitted Late", t: "s", s: { font: { bold: true }, fill: { fgColor: { rgb: "ADD8E6" } } } },
        { v: "Submitted On Time", t: "s", s: { font: { bold: true }, fill: { fgColor: { rgb: "ADD8E6" } } } },
        { v: "Total Submitted", t: "s", s: { font: { bold: true }, fill: { fgColor: { rgb: "ADD8E6" } } } },
        {
          v: "Average Score (on time only)",
          t: "s",
          s: { font: { bold: true }, fill: { fgColor: { rgb: "ADD8E6" } } },
        },
        { v: "Average Score", t: "s", s: { font: { bold: true }, fill: { fgColor: { rgb: "ADD8E6" } } } },
        { v: "Missed Assignments", t: "s", s: { font: { bold: true }, fill: { fgColor: { rgb: "ffcccb" } } } },
      ],
      headerFieldOverall: [
        "full_name",
        "email",
        "submit_late",
        "submit_ontime",
        "total_submited",
        "avg_score_ontime",
        "avg_score",
        "missed",
      ],
      assignmentsTimeline: {},
      assignmentsOverall: {},
      assignmentNames: {},
      disabledExport: true,
      disabledDetailExport: true,
    };
  },
  computed: {
    ...mapGetters({
      getCurrentClass: "classes/getCurrentClass",
      assignments: "assignments/getAssignments",
      pagination: "assignments/buildPagination",
      currentOrganization: "organization/currentOrganization",
      isOrganizationK12: "organization/isOrganizationK12",
    }),
    organizationId() {
      return this.$store.state.organization.currentOrganizationId;
    },
    organizationData() {
      return this.$store.state.organization.currentOrganization;
    },
    pageCounter: always(PAGE_COUNTER),
    numberElementsPerPage: always(NUMBER_ELEMENTS_PER_PAGE),
    ...mapGetters("user", ["isElsaAdmin"]),
    ...mapState("classes", ["exportAssignmentData", "exportAssignmentDetailData", "exportAssignmentDetail"]),
    exportHeaderOrgName() {
      return ["Org Name", this.currentOrganization.name];
    },
    exportHeaderClassName() {
      return ["Class Name", this.getCurrentClass.name];
    },
  },
  destroyed() {
    this.$store.dispatch("classes/resetExportAssignmentData");
    this.$store.dispatch("classes/resetExportAssignmentDetailData");
  },
  methods: {
    createAssNameAndTimeline() {
      this.assignmentNames = {};
      this.assignmentsTimeline = {};
      this.selectedDataSource.forEach(assignment => {
        this.assignmentsTimeline[assignment.id] = [
          "Timeline",
          `${this.formatDate(assignment.start_date)} to ${this.formatDate(assignment.due_date)}`,
        ];
        let ass_name = assignment.name.length <= 31 ? assignment.name : assignment.name.substring(0, 25).concat("...");
        if (Object.values(this.assignmentNames).includes(ass_name)) {
          let dup_name = Object.values(this.assignmentNames).filter(
            name => name.replace(/[(][0-9][)]/, "") == ass_name
          );
          this.assignmentNames[assignment.id] = ass_name.concat(`(${dup_name.length})`);
        } else {
          this.assignmentNames[assignment.id] = ass_name;
        }
      });
    },
    requestExportOverallAssignment(option) {
      return this.getExportOverallAssignmentData(option).catch(err => {
        if (!isSessionExpireError(err)) this.showNoReportDataWarning();
      });
    },
    async fetchExportOverall() {
      const assignmentsRequest = this.selectedDataSource.map(assignment => {
        const totalLearner = assignment["missed"] + assignment["submited_late"] + assignment["submited_ontime"];
        this.assignmentsOverall[assignment.id] = [
          [
            "Max Score",
            this.formatScore(assignment["max_score"]),
            "On time",
            `${assignment["submited_ontime"]}/${totalLearner}`,
          ],
          [
            "Average Score",
            this.formatScore(assignment["avg_score"]),
            "Late",
            `${assignment["submited_late"]}/${totalLearner}`,
          ],
          ["Min Score", this.formatScore(assignment["min_score"]), "Missed", `${assignment["missed"]}/${totalLearner}`],
        ];
        const option = {
          groupId: this.getCurrentClass.id,
          assignment: assignment,
        };
        return this.requestExportOverallAssignment(option);
      });
      await Promise.all(assignmentsRequest).catch(err => {
        if (!isSessionExpireError(err)) this.showNoReportDataWarning();
      });
    },
    learnerOverall(learner_ass, learnerOverallOb) {
      if (!learnerOverallOb) {
        return {
          full_name: learner_ass["full_name"],
          email: learner_ass["email"],
          submit_late: learner_ass["submission"] == "Late" ? 1 : 0,
          submit_ontime: learner_ass["submission"] == "On Time" ? 1 : 0,
          total_submited: learner_ass["status"] == "completed" ? 1 : 0,
          total_score_ontime: learner_ass["submission"] == "On Time" ? learner_ass["highest_score"] : 0,
          total_score:
            learner_ass["highest_score"] && learner_ass["status"] == "completed" ? learner_ass["highest_score"] : 0,
          missed: learner_ass["submission"] == "Missed" ? 1 : 0,
        };
      } else {
        learnerOverallOb["submit_late"] += learner_ass["submission"] == "Late" ? 1 : 0;
        learnerOverallOb["submit_ontime"] += learner_ass["submission"] == "On Time" ? 1 : 0;
        learnerOverallOb["total_submited"] += learner_ass["status"] == "completed" ? 1 : 0;
        learnerOverallOb["total_score_ontime"] +=
          learner_ass["submission"] == "On Time" ? learner_ass["highest_score"] : 0;
        learnerOverallOb["total_score"] +=
          learner_ass["highest_score"] && learner_ass["status"] == "completed" ? learner_ass["highest_score"] : 0;
        learnerOverallOb["missed"] += learner_ass["submission"] == "Missed" ? 1 : 0;
        return learnerOverallOb;
      }
    },
    async doExportOverall() {
      this.loading = true;
      try {
        await this.fetchExportOverall();
        let wb = utils.book_new();
        let sheets = {};
        let overall_report = {};
        for (const id in this.exportAssignmentData) {
          const assignmentNormalized = this.exportAssignmentData[id].map(learner_ass => {
            overall_report[learner_ass["id"]] = this.learnerOverall(learner_ass, overall_report[learner_ass["id"]]);
            let highest_score =
              learner_ass["highest_score"] && learner_ass.status == "completed"
                ? this.formatScore(learner_ass["highest_score"])
                : "";
            let first_date_complete = "--";
            if (learner_ass.status == "completed") {
              first_date_complete = this.formatDateTime(pathOr(null, ["first_date_complete"])(learner_ass));
            }
            const datetime_arr = first_date_complete.split(" ");
            const status = assignmentStatus.find(e => e.value === learner_ass["status"])["label"];
            delete learner_ass["first_date_complete"];
            delete learner_ass["id"];
            return {
              ...learner_ass,
              status: status,
              highest_score: highest_score,
              first_complete_date:
                datetime_arr.length === 4 ? `${datetime_arr[0]} ${datetime_arr[1]} ${datetime_arr[2]}` : "--",
              first_complete_time: datetime_arr[3],
            };
          });
          let headerSection = [
            this.exportHeaderOrgName,
            this.exportHeaderClassName,
            ["Assignment Name", this.assignmentNames[id].replace(/[(][0-9][)]/, "")],
            this.assignmentsTimeline[id],
            ...this.assignmentsOverall[id],
            [],
            this.headerColumnAss,
          ];
          let ws = utils.aoa_to_sheet(headerSection);
          utils.sheet_add_json(ws, assignmentNormalized, { header: this.headerFieldAss, skipHeader: true, origin: -1 });

          const ref = ws["!ref"];
          const rows = ref.split(":")[1].replace(/[A-Z]/g, "");
          for (let i = 10; i <= rows; i++) {
            this.customFormatCell(ws, `E${i}`, "dd MMM yyyy");
          }

          sheets[id] = ws;
        }
        const overall_report_aoo = this.genOverallReportAoo(overall_report);
        const date_export = format(Date.now(), "dd MMM yyyy");
        let overall_ws = utils.aoa_to_sheet([
          this.exportHeaderOrgName,
          this.exportHeaderClassName,
          ["Export Date", date_export],
          ["Number of assignments", this.selectedDataSource.length],
          [],
          this.headerColumnOverall,
        ]);
        this.customFormatCell(overall_ws, "B3", "dd MMM yyyy");
        utils.sheet_add_json(overall_ws, overall_report_aoo, {
          header: this.headerFieldOverall,
          skipHeader: true,
          origin: -1,
        });
        utils.book_append_sheet(wb, overall_ws, "Overall");
        for (var ass_id in sheets) {
          // Error: Sheet name cannot contain : \ / ? * [ ]
          utils.book_append_sheet(wb, sheets[ass_id], this.assignmentNames[ass_id].replace(/[\\/?*[\]]/g, ""));
        }
        writeFile(wb, "Assignment - Overall Report.xlsx");
        this.loading = false;
      } catch (error) {
        this.loading = false;
        throw error;
      } finally {
        this.$store.dispatch("classes/resetExportAssignmentData");
      }
    },
    genOverallReportAoo(overall_report) {
      let overall_report_aoo = [];
      Object.values(overall_report).forEach(learner_overall => {
        let avg_score_ontime =
          learner_overall["total_score_ontime"] > 0
            ? this.formatScore(learner_overall["total_score_ontime"] / learner_overall["submit_ontime"])
            : "";
        let avg_score =
          learner_overall["total_submited"] > 0
            ? this.formatScore(learner_overall["total_score"] / learner_overall["total_submited"])
            : "";
        delete learner_overall["total_score"];
        delete learner_overall["total_score_ontime"];
        let new_learner_overall = { ...learner_overall, avg_score_ontime, avg_score };
        overall_report_aoo.push(new_learner_overall);
      });
      return overall_report_aoo;
    },
    requestExportDetailAssignment(option) {
      return this.getExportDetailAssignmentData(option).catch(err => {
        if (!isSessionExpireError(err)) this.showNoReportDataWarning();
      });
    },
    async fetchExportDetail() {
      const assignmentsRequest = this.selectedDataSource.map(assignment => {
        this.assignmentsTimeline[assignment.id] = [
          "Timeline",
          `${this.formatDate(assignment.start_date)} to ${this.formatDate(assignment.due_date)}`,
        ];
        const option = {
          groupId: this.getCurrentClass.id,
          assignment: assignment,
        };
        return this.requestExportDetailAssignment(option);
      });
      await Promise.all(assignmentsRequest).catch(err => {
        if (!isSessionExpireError(err)) this.showNoReportDataWarning();
      });
    },
    async doExportDetail() {
      this.loading = true;
      try {
        await this.fetchExportDetail();

        const date_export = format(Date.now(), "dd MMM yyyy");
        let wb = utils.book_new();

        for (const id in this.exportAssignmentDetailData) {
          let assignmentNormalized = [];
          let lessonAssignmentNormalized = [];
          let assignmentType;
          let largestSSTime = 0;
          let wsStudySetData = {};
          let is4SkillSS = false;
          const assignmentDetail = this.exportAssignmentDetail[id];

          for (const data of this.exportAssignmentDetailData[id]) {
            assignmentType = data["assignment_type"].join("+");

            if (assignmentType === "lesson") {
              const detailData = processLessonDetailedReportData(
                data,
                this.currentOrganization.timezone,
                assignmentDetail
              );
              assignmentNormalized.push(...detailData);

              const lessonData = processLessonAssignmentsData(
                data,
                this.currentOrganization.timezone,
                assignmentDetail
              );
              lessonAssignmentNormalized.push(...lessonData);
            } else if (assignmentType === "study_set") {
              const processedData = processSSDetailedReportData(
                data,
                this.currentOrganization.timezone,
                assignmentDetail,
                largestSSTime,
                wsStudySetData
              );
              if (processedData["is4SkillSS"]) {
                is4SkillSS = true;
              }
              largestSSTime = processedData["largestSSTime"];
              assignmentNormalized.push(...processedData["assignmentNormalized"]);
            } else if (assignmentType.includes("read_aloud")) {
              const detail = data["detail"].sort((a, b) => a.assignment_detail_id - b.assignment_detail_id);

              const raData = processReadAloudDetailedReportData(
                data,
                this.currentOrganization.timezone,
                assignmentDetail,
                detail
              );
              assignmentNormalized.push(...raData);

              const lessonData = processReadAloudLessonAssignmentsData(
                data,
                this.currentOrganization.timezone,
                assignmentDetail,
                detail
              );
              lessonAssignmentNormalized.push(...lessonData);
            } else if (assignmentType.includes("ai_tutor")) {
              const aiTutorData = processAITutorDetailedReportData(
                data,
                this.currentOrganization.timezone,
                assignmentDetail
              );
              assignmentNormalized.push(...aiTutorData);

              const lessonData = processAITutorAssignmentsData(
                data,
                this.currentOrganization.timezone,
                assignmentDetail
              );
              lessonAssignmentNormalized.push(...lessonData);
            }
          }

          // tab 1: Assignment Detailed Report
          let defaultHeaderSection = [
            this.exportHeaderOrgName,
            this.exportHeaderClassName,
            ["Assignment Name", this.assignmentNames[id].replace(/[(][0-9][)]/, "")],
            this.assignmentsTimeline[id],
            ["Export Date", date_export],
          ];

          let headerSection = [
            ...defaultHeaderSection,
            this.getDetailHeaderByAssignmentType(assignmentType, { largestSSTime }),
          ];

          let ws = utils.aoa_to_sheet(headerSection);

          utils.sheet_add_json(ws, assignmentNormalized, {
            skipHeader: true,
            origin: -1,
          });

          this.customFormatCell(ws, "B5", "dd MMM yyyy");

          utils.book_append_sheet(wb, ws, "Assignment Detailed Report");

          if (assignmentType === "lesson" && lessonAssignmentNormalized.length > 0) {
            // Generate tab 2: Lesson Assignments
            headerSection = [
              ...defaultHeaderSection,
              [
                { v: "Student Name", t: "s", s: { font: { bold: true } } },
                { v: "Student Email", t: "s", s: { font: { bold: true } } },
                { v: "Study Attempt", t: "s", s: { font: { bold: true } } },
                { v: "Completion Date-Time", t: "s", s: { font: { bold: true } } },
                { v: "Lesson Title", t: "s", s: { font: { bold: true } } },
                { v: "Skill Type", t: "s", s: { font: { bold: true } } },
                { v: "Level Of Difficulty", t: "s", s: { font: { bold: true } } },
                { v: "Score", t: "s", s: { font: { bold: true } } },
              ],
            ];
            this.generateTabLessonAssignment(headerSection, wb, lessonAssignmentNormalized);
          } else if (assignmentType === "study_set") {
            // Generate tab 2: Study Set Assignment - Item Key
            this.generateSheetSSItem(this.exportAssignmentDetail[id], id, date_export, wb);
            // Generate tab n: <Study Set Title> - Detailed Score Report
            this.generateSheetSSItemDetail(wsStudySetData, is4SkillSS, id, date_export, wb);
          } else if (assignmentType.includes("read_aloud") && lessonAssignmentNormalized.length > 0) {
            // Generate tab 2: Lesson Assignments
            headerSection = [
              ...defaultHeaderSection,
              [
                { v: "Student Name", t: "s", s: { font: { bold: true } } },
                { v: "Student Email", t: "s", s: { font: { bold: true } } },
                { v: "Study Attempt", t: "s", s: { font: { bold: true } } },
                { v: "Completion Date-Time", t: "s", s: { font: { bold: true } } },
                { v: "Lesson Title", t: "s", s: { font: { bold: true } } },
                { v: "Skill Type", t: "s", s: { font: { bold: true } } },
                { v: "Level Of Difficulty", t: "s", s: { font: { bold: true } } },
                { v: "Score", t: "s", s: { font: { bold: true } } },
                { v: "Overall (EPS)", t: "s", s: { font: { bold: true } } },
                { v: "Fluency Score", t: "s", s: { font: { bold: true } } },
                { v: "Pronunciation Score", t: "s", s: { font: { bold: true } } },
                { v: "Intonation Score", t: "s", s: { font: { bold: true } } },
              ],
            ];
            this.generateTabLessonAssignment(headerSection, wb, lessonAssignmentNormalized);
          } else if (assignmentType.includes("ai_tutor") && lessonAssignmentNormalized.length > 0) {
            // Generate tab 2: Lesson Assignments
            headerSection = [
              ...defaultHeaderSection,
              [
                { v: "Student Name", t: "s", s: { font: { bold: true } } },
                { v: "Student Email", t: "s", s: { font: { bold: true } } },
                { v: "Study Attempt", t: "s", s: { font: { bold: true } } },
                { v: "Completion Date-Time", t: "s", s: { font: { bold: true } } },
                { v: "Lesson Title", t: "s", s: { font: { bold: true } } },
                { v: "Skill Type", t: "s", s: { font: { bold: true } } },
                { v: "Level Of Difficulty", t: "s", s: { font: { bold: true } } },
                { v: "Lesson Score", t: "s", s: { font: { bold: true } } },
                { v: "Goals Completed", t: "s", s: { font: { bold: true } } },
                { v: "Pronunciation", t: "s", s: { font: { bold: true } } },
                { v: "Vocabulary", t: "s", s: { font: { bold: true } } },
                { v: "Grammar", t: "s", s: { font: { bold: true } } },
                { v: "Hints", t: "s", s: { font: { bold: true } } },
                // { v: "User Turns", t: "s", s: { font: { bold: true } } },
                { v: "Duration", t: "s", s: { font: { bold: true } } },
              ],
            ];
            this.generateTabLessonAssignment(headerSection, wb, lessonAssignmentNormalized);
          }
        }
        writeFile(wb, "Assignment - Detailed Report.xlsx");
        this.loading = false;
      } catch (error) {
        this.loading = false;
        throw error;
      } finally {
        this.$store.dispatch("classes/resetExportAssignmentDetailData");
      }
    },
    ...mapActions({
      getAssignments: "assignments/getAssignments",
      deleteAssignment: "assignments/deleteAssignment",
    }),
    ...mapActions("classes", ["getExportOverallAssignmentData", "getExportDetailAssignmentData"]),
    async deleteAssignmentAction(assignmentId) {
      return this.deleteAssignment(assignmentId);
    },
    handleDeleteStudySet(studySetId) {
      this.modals.showConfirmDelete = true;
      this.deleteStudySetIds = [studySetId];
    },
    handleDeleteSelectedStudySets() {
      this.modals.showConfirmDelete = true;
      this.deleteStudySetIds = this.selectedDataSource.map(x => x.id);
    },
    async anyStudySet() {
      this.loading = true;
      const { data } = await this.$axios.get(`/v1/study-sets/${this.organizationId}/`);
      this.loading = false;
      return data && data.results && data.results.length;
    },
    async handleAddAssignment() {
      const organizationId = this.organizationId;

      return this.$router.push({
        name: "assignment-new",
        params: {
          organizationId,
        },
      });
    },
    handleExit() {
      this.$refs.table.reloadAndRest();
    },
    async handleConfirmDeleteAssignment() {
      try {
        for (let idx = 0; idx < this.deleteStudySetIds.length; idx++) {
          await this.deleteAssignment(this.deleteStudySetIds[idx]);
        }
        successMessage(this.$t("assignments.delete_assignment_success"))(this);
      } catch (error) {
        return warningMessage(this.$t("assignments.delete_assignment_warning"))(this);
      } finally {
        this.deleteStudySetIds = [];
        await this.getDataSetPage(this.currentPage);
      }
    },
    handleCreateStudySet() {
      return this.$router.push({
        name: "studyset-new",
        params: {
          organizationId: this.currentOrganizationId,
        },
      });
    },
    searchData() {
      return this.getDatas({});
    },
    getDataSetPage(page) {
      const search = this.queryString;
      return this.getDatas({ page, search });
    },
    async getDatas({ page = 1, status = "" }) {
      this.loading = true;
      const classId = this.groupId;
      let params = { classId, page, search: this.queryString, status, ordering: this.ordering };
      await this.getAssignments(params);
      this.selectedDataSource = [];
      this.loading = false;
    },
    formatDate(date) {
      if (!date) return "--";
      const dateFormat = "dd MMM yyyy";
      let timeZone = this.currentOrganization.timezone;
      const zonedDate = utcToZonedTime(date, timeZone);
      return date ? format(zonedDate, dateFormat) : "--";
    },
    formatScore(score) {
      return score != 0 ? Math.round(score) + "%" : "--";
    },
    formatDateTime(date) {
      const dateFormat = "dd MMM yyyy HH:mm";
      if (!date) return "--";
      let timeZone = this.currentOrganization.timezone;
      const zonedDate = utcToZonedTime(date, timeZone);
      return date ? format(zonedDate, dateFormat) : "--";
    },
    handleCommand(command) {
      this.createAssNameAndTimeline();
      switch (command) {
        case "overall":
          this.doExportOverall();
          break;

        case "detailed":
          this.doExportDetail();
          break;

        case "both":
          this.doExportOverall();
          this.doExportDetail();
          break;

        default:
          break;
      }
    },
    sortFuncSpec() {
      return this.getDatas({});
    },
    customFormatCell(ws, cell, format = "dd MMM yyyy") {
      if (ws[cell] && ws[cell].t === "s") {
        ws[cell].z = format;
      }
    },
    numberToOrdinalString(number) {
      if (number === 0) {
        return "0"; // Handle zero as a special case
      }

      const lastDigit = number % 10;
      const secondLastDigit = Math.floor((number % 100) / 10);

      if (secondLastDigit === 1 || lastDigit === 0 || lastDigit >= 4) {
        return number + "th";
      } else if (lastDigit === 1) {
        return number + "st";
      } else if (lastDigit === 2) {
        return number + "nd";
      } else if (lastDigit === 3) {
        return number + "rd";
      }
    },
    generateSheetSSItemDetail(data, is4SkillSS, id, date_export, wb) {
      for (const key in data) {
        const element = data[key];
        let scoreHeaderSection = [
          { v: "Student Name", t: "s", s: { font: { bold: true } } },
          { v: "Student Email", t: "s", s: { font: { bold: true } } },
          { v: "Study Attempt", t: "s", s: { font: { bold: true } } },
          { v: "Completion Date-time", t: "s", s: { font: { bold: true } } },
          { v: "Item", t: "s", s: { font: { bold: true } } },
          { v: "Item Score", t: "s", s: { font: { bold: true } } },
        ];

        if (is4SkillSS) {
          scoreHeaderSection.push(
            ...[
              { v: "Speaking", t: "s", s: { font: { bold: true } } },
              { v: "Listening", t: "s", s: { font: { bold: true } } },
              { v: "Reading", t: "s", s: { font: { bold: true } } },
              { v: "Writing", t: "s", s: { font: { bold: true } } },
            ]
          );
        } else {
          if (element.hasOwnProperty("data")) {
            for (const item of element.data) {
              delete item.speaking;
              delete item.listening;
              delete item.reading;
              delete item.writing;
            }
          }
        }

        let headerSection = [
          this.exportHeaderOrgName,
          this.exportHeaderClassName,
          ["Assignment Name", this.assignmentNames[id].replace(/[(][0-9][)]/, "")],
          this.assignmentsTimeline[id],
          ["Export Date", date_export],
          scoreHeaderSection,
        ];
        let ws = utils.aoa_to_sheet(headerSection);
        utils.sheet_add_json(ws, element["data"], {
          skipHeader: true,
          origin: -1,
        });
        this.customFormatCell(ws, "B5", "dd MMM yyyy");
        // Error: Sheet name cannot contain : \ / ? * [ ]
        utils.book_append_sheet(wb, ws, this.generateUniqueSheetName(wb, element["name"]));
      }
    },
    generateUniqueSheetName(wb, name) {
      const MAX_SHEET_NAME_LENGTH = 31; // Maximum allowed length for sheet names
      let uniqueName = name.replace(/[\\/?*[\]]/g, "").slice(0, MAX_SHEET_NAME_LENGTH); // Remove invalid characters and truncate to 31 chars
      let counter = 1;
      let existingSheetNames = wb.SheetNames;

      while (existingSheetNames.includes(uniqueName)) {
        const counterStr = ` (${counter})`;
        const maxNameLength = MAX_SHEET_NAME_LENGTH - counterStr.length;
        uniqueName = `${name.replace(/[\\/?*[\]]/g, "").slice(0, maxNameLength)}${counterStr}`;
        counter++;
      }

      return uniqueName;
    },
    generateSheetSSItem(wsData, id, date_export, wb) {
      let data = [];

      wsData.forEach(element => {
        const init = {
          ss_title: element["name"],
        };

        element["phrases"].forEach((value, index) => {
          const row = {
            ...init,
            item_number: index + 1,
            item_text: value,
          };

          data.push(row);
        });
      });

      let subHeaderSection = [
        { v: "Study Set Title", t: "s", s: { font: { bold: true } } },
        { v: "Item Number", t: "s", s: { font: { bold: true } } },
        { v: "Item Text", t: "s", s: { font: { bold: true } } },
      ];

      let headerSection = [
        this.exportHeaderOrgName,
        this.exportHeaderClassName,
        ["Assignment Name", this.assignmentNames[id].replace(/[(][0-9][)]/, "")],
        this.assignmentsTimeline[id],
        ["Export Date", date_export],
        subHeaderSection,
      ];
      let ws = utils.aoa_to_sheet(headerSection);
      utils.sheet_add_json(ws, data, {
        skipHeader: true,
        origin: -1,
      });
      this.customFormatCell(ws, "B5", "dd MMM yyyy");
      // Error: Sheet name cannot contain : \ / ? * [ ]
      utils.book_append_sheet(wb, ws, "Study Set Assignment - Item Key");
    },
    getDetailHeaderByAssignmentType(type, options = {}) {
      let header;

      switch (type) {
        case "study_set":
          header = [
            { v: "Student Name", t: "s", s: { font: { bold: true } } },
            { v: "Student Email", t: "s", s: { font: { bold: true } } },
            { v: "Study Set Title", t: "s", s: { font: { bold: true } } },
            { v: "Study Set Highest Score", t: "s", s: { font: { bold: true } } },
            { v: "Study Set Status", t: "s", s: { font: { bold: true } } },
            { v: "Completion Date - First Time", t: "s", s: { font: { bold: true } } },
            { v: "Number of Attempts", t: "s", s: { font: { bold: true } } },
            { v: "Study Set Avg. Score", t: "s", s: { font: { bold: true } } },
            { v: "Study Set Score - 1st Time", t: "s", s: { font: { bold: true } } },
          ];

          for (let idx = 1; idx < options.largestSSTime; idx++) {
            const formattedString = this.numberToOrdinalString(idx + 1);

            header.push(
              ...[
                {
                  v: `Completion Date - ${formattedString} Time`,
                  t: "s",
                  s: { font: { bold: true } },
                },
                {
                  v: `Study Set Score - ${formattedString} Time`,
                  t: "s",
                  s: { font: { bold: true } },
                },
              ]
            );
          }
          break;

        case "lesson":
          header = [
            { v: "Student Name", t: "s", s: { font: { bold: true } } },
            { v: "Student Email", t: "s", s: { font: { bold: true } } },
            { v: "Lesson Title", t: "s", s: { font: { bold: true } } },
            { v: "Highest Score", t: "s", s: { font: { bold: true } } },
            { v: "Study Status", t: "s", s: { font: { bold: true } } },
            { v: "Completion Date - First Time", t: "s", s: { font: { bold: true } } },
          ];
          break;

        case "ai_tutor":
          header = [
            { v: "Student Name", t: "s", s: { font: { bold: true } } },
            { v: "Student Email", t: "s", s: { font: { bold: true } } },
            { v: "Lesson Title", t: "s", s: { font: { bold: true } } },
            { v: "Assignment Score", t: "s", s: { font: { bold: true } } },
            { v: "Study Status", t: "s", s: { font: { bold: true } } },
            { v: "Completion Date - First Time", t: "s", s: { font: { bold: true } } },
            { v: "Number of Attempts", t: "s", s: { font: { bold: true } } },
            { v: "Avg. Score (All Attempts)", t: "s", s: { font: { bold: true } } },
          ];
          break;

        default:
          header = [
            { v: "Student Name", t: "s", s: { font: { bold: true } } },
            { v: "Student Email", t: "s", s: { font: { bold: true } } },
            { v: "Lesson Title", t: "s", s: { font: { bold: true } } },
            { v: "Highest Score", t: "s", s: { font: { bold: true } } },
            { v: "Study Status", t: "s", s: { font: { bold: true } } },
            { v: "Completion Date - First Time", t: "s", s: { font: { bold: true } } },
          ];
          break;
      }

      return header;
    },
    generateTabLessonAssignment(headerSection, wb, dataNormalized) {
      let ws2 = utils.aoa_to_sheet(headerSection);
      utils.sheet_add_json(ws2, dataNormalized, {
        skipHeader: true,
        origin: -1,
      });
      this.customFormatCell(ws2, "B5", "dd MMM yyyy");
      utils.book_append_sheet(wb, ws2, "Lesson Assignments");
    },
  },
  watch: {
    selectedDataSource(newVal) {
      this.disabledExport = !(newVal.length > 0);
      this.disabledDetailExport = newVal.length > 1;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../scss/_colors.scss";
.assignment-container {
  width: 100%;
  font-family: "SF Pro Display", sans-serif;
  .assignment-tab-body {
    padding: 24px 0px;
    text-align: center;
    padding-bottom: 0px;
  }
  .create-new-assignment {
    button {
      background: #007eff;
      border-radius: 8px;
      border: none;
      color: #fff;
      padding: 11px;
      font-size: 16px;
      line-height: 24px;
      font-family: "SF Pro Display";
      font-weight: 600;
      max-width: 230px;
      cursor: pointer;
    }
  }
  .el-table .el-table__row .wrap-avatar small {
    font-size: 16px;
  }
  .no-data {
    margin-top: 72px;
    display: flex;
    flex-direction: column;
    align-items: baseline;
    p {
      margin: 0;
      font-weight: 500;
      font-size: 18px;
      color: $dark-blue-2;
    }
    img {
      margin-top: 50px;
      margin-bottom: 63px;
    }
  }
  .status__label {
    font-size: 16px;
    text-transform: capitalize;
    padding-left: 10px;
  }
  .status__input {
    vertical-align: middle;
    .status__inner {
      border: 1px solid #dcdfe6;
      border-radius: 100%;
      width: 14px;
      height: 14px;
      background-color: #fff;
      cursor: pointer;
      -webkit-box-sizing: border-box;
      box-sizing: border-box;
      position: relative;
      display: inline-block;
    }
  }
  .status__active .status__inner {
    border-color: #03bd23;
    background-color: #03bd23;
  }
  .status__finished .status__inner {
    border-color: #e6a23c;
    background-color: #e6a23c;
  }
  .status__not_started .status__inner {
    border-color: #909399;
    background-color: #909399;
  }
  .status__pending {
    display: block;
  }
  .status__pending .status__inner {
    border-color: #d9ecff;
    background-color: #d9ecff;
  }
  .wrap-avatar {
    .block {
      word-break: break-word;
    }
  }
  .el-menu.sidemenu {
    .el-menu-item {
      border: none;
      margin-top: 8px;
      margin-bottom: 24px;
      height: 48px !important;
      span {
        text-transform: capitalize;
      }
      svg {
        width: 30px;
      }
    }
  }
  .elsa-table-layout-header {
    display: flex;
    justify-content: flex-end;
    width: 80%;
  }
  .el-form {
    width: auto;
  }
  .el-input {
    border: 1px solid #d8d8d8;
    border-radius: 8px;
  }
  .assign-to-class {
    margin-right: 8px;
  }
  .remove-assignment {
    color: red;
  }
  .export-report {
    margin-right: 20px;

    .el-dropdown {
      .el-button {
        background: #007eff;
        border-radius: 8px;
        border: none;
        color: #fff;
        padding: 11px;
        font-size: 16px;
        line-height: 24px;
        font-family: "SF Pro Display";
        font-weight: 600;
        max-width: 230px;
        cursor: pointer;

        &.is-disabled {
          color: #fff;
          background-color: #a0cfff;
          border-color: #a0cfff;
        }
      }
    }
  }
}

::v-deep .el-popper {
  margin-left: -75px !important;
  margin-top: 40px !important;
}

::v-deep .el-table__header {
  table-layout: auto !important;
}

::v-deep .el-table__body,
.el-table__footer,
.el-table__header {
  table-layout: auto;
}

::v-deep .elsa-table-layout {
  padding-top: 0px;
}

::v-deep .el-table__column-filter-trigger i {
  font-size: 16px;
}

::v-deep .el-table .cell {
  padding: 12px 0px !important;
}
.elsa-checkbox {
  justify-content: center;
}
.close-button {
  width: 40px !important;
  height: 40px !important;
  min-width: 40px;
}
.el-icon-close {
  font-weight: 900;
  font-size: 16px;
}

.el-button.delete-assignment {
  color: #eb5757;
  font-size: 16px;
  line-height: 24px;
  font-family: "SF Pro Display";
  font-weight: 600;
}

::v-deep .el-table__body,
::v-deep .el-table__footer,
::v-deep .el-table__header {
  //table-layout: auto !important;
  width: 100% !important;
}

::v-deep .elsa-table-layout {
  padding-top: 0px;
}

::v-deep .el-input {
  height: 48px !important;
}

::v-deep .el-input-group__append {
  border-radius: 40px !important;
}

::v-deep .el-table__empty-block {
  width: 100% !important;
}

::v-deep .el-button--text {
  font-size: 16px;
  line-height: 24px;
  font-family: "SF Pro Display";
  font-weight: 600;
}
::v-deep .el-button.is-plain:hover {
  border-color: rgb(235, 87, 87);
}
</style>
